import type { IFlatViewLauncherData } from './flat-view-launcher.component';

/**
 * The Visualize launcher remembers the inputs of its last run, per browser, and offers them on
 * the next pattern opened, whichever pattern that is. Macros keep a length of their own: one is
 * drawn as a single call in the middle of the part, so it wants a short length where a pattern
 * wants a real part length.
 */
const STORAGE_KEY = 'flatViewLauncher.last';

export interface ILauncherMemory {
  machineNumber?: number | null;
  toolingCode?: string;
  materialCode?: string;
  lengthText?: string;
  macroLengthText?: string;
}

export interface ILauncherSelections {
  machineNumber: number | null;
  toolingCode: string;
  materialText: string;
  lengthText: string;
}

export function readLauncherMemory(): ILauncherMemory {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    // Only fields of the expected type: a value edited by hand, or left by an older format,
    // would otherwise break the launcher's starting values.
    const memory: ILauncherMemory = {};
    if (typeof parsed?.machineNumber === 'number') {
      memory.machineNumber = parsed.machineNumber;
    }
    for (const key of ['toolingCode', 'materialCode', 'lengthText', 'macroLengthText'] as const) {
      if (typeof parsed?.[key] === 'string') {
        memory[key] = parsed[key];
      }
    }
    return memory;
  } catch {
    return {}; // storage blocked or unreadable: start empty
  }
}

export function writeLauncherMemory(selections: ILauncherSelections, isMacro: boolean): void {
  try {
    const memory: ILauncherMemory = {
      ...readLauncherMemory(),
      machineNumber: selections.machineNumber,
      toolingCode: selections.toolingCode.trim(),
      materialCode: selections.materialText.trim(),
    };
    memory[isMacro ? 'macroLengthText' : 'lengthText'] = selections.lengthText.trim();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
  } catch {
    // Storage blocked or full: the launcher just won't remember.
  }
}

/**
 * The launcher's starting values. What the caller knows (an order line's machine, tooling,
 * material and length) wins and the last run fills the rest; a macro's suggested length comes
 * last. The machine and tooling code are remembered as a pair, so a remembered tooling code is
 * never put next to a different machine the caller chose. When the caller gives a tooling code
 * but no machine, the machine stays empty here and the launcher picks one that runs it,
 * preferring the remembered one.
 */
export function initialSelections(
  data: IFlatViewLauncherData,
  memory: ILauncherMemory,
): ILauncherSelections {
  const lastMachine = typeof memory.machineNumber === 'number' ? memory.machineNumber : null;
  const lastTooling = memory.toolingCode?.trim() ?? '';
  const callerTooling = data.toolingCode?.trim() ?? '';

  let machineNumber: number | null;
  let toolingCode: string;
  if (callerTooling) {
    machineNumber = data.machineNumber ?? null;
    toolingCode = callerTooling;
  } else if (data.machineNumber != null) {
    machineNumber = data.machineNumber;
    toolingCode = data.machineNumber === lastMachine ? lastTooling : '';
  } else {
    machineNumber = lastMachine;
    toolingCode = lastTooling;
  }

  const lastLength = (data.macroKind ? memory.macroLengthText : memory.lengthText)?.trim();
  // Bare numbers parse as inches (parseLengthToInches), so a raw value round-trips.
  const lengthText =
    data.lengthIn > 0
      ? String(data.lengthIn)
      : lastLength || (data.suggestedLengthIn > 0 ? String(data.suggestedLengthIn) : '');

  return {
    machineNumber,
    toolingCode,
    materialText: data.materialCode?.trim() || memory.materialCode?.trim() || '',
    lengthText,
  };
}
